import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  ArrowLeft, Save, Plus, X, UserPlus, Users,
  Mail, Phone, Building, Globe, Linkedin, Edit2, Share2
} from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactGroupForm = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const isEdit = !!groupId;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contacts: []
  });

  const [myContacts, setMyContacts] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const [newContact, setNewContact] = useState({
    name: '',
    businessName: '',
    title: '',
    email: '',
    phone: '',
    mobile: '',
    website: '',
    address: '',
    linkedin: '',
    socialMedia: [] // Array of {platform: '', url: ''}
  });

  useEffect(() => {
    loadMyContacts();
    if (isEdit) {
      loadGroup();
    }
  }, [groupId]);

  const loadMyContacts = async () => {
    try {
      // Load from localStorage (My Connections)
      const scannedContacts = JSON.parse(localStorage.getItem('scannedContacts') || '[]');
      setMyContacts(scannedContacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  const loadGroup = async () => {
    try {
      const response = await axios.get(`${API}/contact-groups/${groupId}`, {
        headers: getAuthHeaders()
      });
      const group = response.data.group;
      setFormData({
        name: group.name,
        description: group.description || '',
        contacts: group.contacts || []
      });
    } catch (error) {
      toast.error('Failed to load group');
      console.error(error);
    }
  };

  const handleAddFromMyContacts = (contact) => {
    // Check if already added
    const exists = formData.contacts.some(c => c.email === contact.email && c.name === contact.name);
    if (exists) {
      toast.error('Contact already added to group');
      return;
    }

    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, {
        name: contact.name || '',
        businessName: contact.businessName || '',
        title: contact.title || '',
        email: contact.email || '',
        phone: contact.phone || contact.mobile || '',
        mobile: contact.mobile || '',
        website: contact.website || '',
        address: contact.address || '',
        linkedin: contact.linkedin || ''
      }]
    }));
    toast.success('Contact added!');
  };

  const handleAddManualContact = () => {
    if (!newContact.name) {
      toast.error('Name is required');
      return;
    }

    // Check if already added
    const exists = formData.contacts.some(c => 
      c.email === newContact.email && c.name === newContact.name
    );
    if (exists) {
      toast.error('Contact already added to group');
      return;
    }

    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { ...newContact }]
    }));

    // Reset form
    setNewContact({
      name: '',
      businessName: '',
      title: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      address: '',
      linkedin: '',
      socialMedia: []
    });

    setShowManualAdd(false);
    toast.success('Contact added!');
  };

  const handleAddSocialMedia = () => {
    setNewContact(prev => ({
      ...prev,
      socialMedia: [...(prev.socialMedia || []), { platform: '', url: '' }]
    }));
  };

  const handleRemoveSocialMedia = (index) => {
    setNewContact(prev => ({
      ...prev,
      socialMedia: prev.socialMedia.filter((_, i) => i !== index)
    }));
  };

  const handleSocialMediaChange = (index, field, value) => {
    setNewContact(prev => ({
      ...prev,
      socialMedia: prev.socialMedia.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleEditContact = (index) => {
    setEditingContactIndex(index);
    const contact = {...formData.contacts[index]};
    // Ensure socialMedia array exists
    if (!contact.socialMedia) {
      contact.socialMedia = [];
    }
    setNewContact(contact);
    setShowEditContact(true);
  };

  const handleSaveEditContact = () => {
    if (!newContact.name || !newContact.email) {
      toast.error('Name and email are required');
      return;
    }

    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((contact, i) => 
        i === editingContactIndex ? {...newContact} : contact
      )
    }));

    setShowEditContact(false);
    setEditingContactIndex(null);
    setNewContact({
      name: '',
      businessName: '',
      title: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      address: '',
      linkedin: '',
      socialMedia: []
    });
    toast.success('Contact updated!');
  };

  const handleRemoveContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
    toast.success('Contact removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      toast.error('Group name is required');
      return;
    }

    if (formData.contacts.length === 0) {
      toast.error('Add at least one contact to the group');
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/contact-groups/${groupId}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Group updated successfully!');
      } else {
        await axios.post(`${API}/contact-groups`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Group created successfully!');
      }
      navigate('/contact-groups');
    } catch (error) {
      toast.error(isEdit ? 'Failed to update group' : 'Failed to create group');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/contact-groups')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            {isEdit ? 'Edit Contact Group' : 'Create Contact Group'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Group Info */}
          <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Group Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Group Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Sales Team, Event Attendees, Family"
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this group..."
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </Card>

          {/* Contacts */}
          <Card className="p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">Contacts ({formData.contacts.length})</h2>
                <p className="text-sm text-slate-400 mt-1">Add contacts to this group</p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => setShowContactPicker(true)}
                  size="sm"
                  className="bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Users className="w-4 h-4 mr-2" />
                  From My Contacts
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowManualAdd(true)}
                  size="sm"
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Manually
                </Button>
              </div>
            </div>

            {/* Contacts List */}
            {formData.contacts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-lg">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 mb-4">No contacts added yet</p>
                <div className="flex gap-3 justify-center">
                  <Button
                    type="button"
                    onClick={() => setShowContactPicker(true)}
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Select from My Contacts
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setShowManualAdd(true)}
                    size="sm"
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Add Manually
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.contacts.map((contact, index) => (
                  <div key={index} className="p-4 bg-slate-700/50 border border-slate-600 rounded-lg flex items-start justify-between group hover:border-cyan-500 transition-colors">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white mb-1">{contact.name}</h3>
                      {contact.businessName && (
                        <p className="text-sm text-slate-400">{contact.businessName}</p>
                      )}
                      <div className="mt-2 space-y-1 text-sm text-slate-300">
                        {contact.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-cyan-400" />
                            <span>{contact.email}</span>
                          </div>
                        )}
                        {contact.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-cyan-400" />
                            <span>{contact.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        type="button"
                        onClick={() => handleEditContact(index)}
                        size="sm"
                        variant="ghost"
                        className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleRemoveContact(index)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Submit */}
          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold shadow-lg shadow-cyan-500/50"
            >
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Saving...' : (isEdit ? 'Update Group' : 'Create Group')}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/contact-groups')}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white px-8"
            >
              Cancel
            </Button>
          </div>
        </form>

        {/* Contact Picker Modal */}
        {showContactPicker && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowContactPicker(false)}>
            <Card className="bg-slate-800 border border-slate-700 p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold text-white mb-4">Select from My Contacts</h3>
              
              {myContacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 mb-4">No contacts in My Connections yet</p>
                  <Button
                    onClick={() => {
                      setShowContactPicker(false);
                      navigate('/digi-connect');
                    }}
                    size="sm"
                  >
                    Go to DigiConnect
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {myContacts.map((contact, index) => (
                    <div
                      key={index}
                      onClick={() => {
                        handleAddFromMyContacts(contact);
                        setShowContactPicker(false);
                      }}
                      className="p-3 bg-slate-700/50 border border-slate-600 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-slate-700 transition-all"
                    >
                      <h4 className="font-semibold text-white">{contact.name}</h4>
                      {contact.businessName && (
                        <p className="text-sm text-slate-400">{contact.businessName}</p>
                      )}
                      {contact.email && (
                        <p className="text-xs text-slate-500 mt-1">{contact.email}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={() => setShowContactPicker(false)}
                variant="outline"
                className="w-full mt-4 border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                Close
              </Button>
            </Card>
          </div>
        )}

        {/* Manual Add Modal */}
        {showManualAdd && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowManualAdd(false)}>
            <Card className="bg-slate-800 border border-slate-700 p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold text-white mb-4">Add Contact Manually</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={newContact.businessName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, businessName: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={newContact.title}
                    onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Job Title"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Email</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Phone Number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Website</label>
                  <input
                    type="url"
                    value={newContact.website}
                    onChange={(e) => setNewContact(prev => ({ ...prev, website: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Address</label>
                  <input
                    type="text"
                    value={newContact.address}
                    onChange={(e) => setNewContact(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">LinkedIn</label>
                  <input
                    type="url"
                    value={newContact.linkedin}
                    onChange={(e) => setNewContact(prev => ({ ...prev, linkedin: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="LinkedIn URL"
                  />
                </div>

                {/* Social Media Links Section */}
                <div className="border-t border-slate-600 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-slate-300">Social Media Links</label>
                    <Button
                      type="button"
                      onClick={handleAddSocialMedia}
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Link
                    </Button>
                  </div>
                  
                  {newContact.socialMedia && newContact.socialMedia.length > 0 && (
                    <div className="space-y-3">
                      {newContact.socialMedia.map((social, index) => (
                        <div key={index} className="flex gap-2">
                          <select
                            value={social.platform}
                            onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)}
                            className="w-1/3 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                          >
                            <option value="">Select Platform</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Twitter">Twitter/X</option>
                            <option value="TikTok">TikTok</option>
                            <option value="YouTube">YouTube</option>
                            <option value="Pinterest">Pinterest</option>
                            <option value="Snapchat">Snapchat</option>
                            <option value="WhatsApp">WhatsApp</option>
                            <option value="Telegram">Telegram</option>
                            <option value="Discord">Discord</option>
                            <option value="Reddit">Reddit</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            type="url"
                            value={social.url}
                            onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                            placeholder="https://..."
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                          />
                          <Button
                            type="button"
                            onClick={() => handleRemoveSocialMedia(index)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleAddManualContact}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Contact
                </Button>
                <Button
                  onClick={() => setShowManualAdd(false)}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Edit Contact Modal */}
        {showEditContact && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowEditContact(false)}>
            <Card className="bg-slate-800 border border-slate-700 p-6 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-bold text-white mb-4">Edit Contact</h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Name *</label>
                    <input
                      type="text"
                      value={newContact.name}
                      onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Business Name</label>
                    <input
                      type="text"
                      value={newContact.businessName}
                      onChange={(e) => setNewContact(prev => ({ ...prev, businessName: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Company"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={newContact.title}
                    onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Job Title"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Email *</label>
                    <input
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={newContact.phone}
                      onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Phone Number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Mobile</label>
                    <input
                      type="tel"
                      value={newContact.mobile}
                      onChange={(e) => setNewContact(prev => ({ ...prev, mobile: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="Mobile Number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Website</label>
                    <input
                      type="url"
                      value={newContact.website}
                      onChange={(e) => setNewContact(prev => ({ ...prev, website: e.target.value }))}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Address</label>
                  <input
                    type="text"
                    value={newContact.address}
                    onChange={(e) => setNewContact(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="Address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">LinkedIn</label>
                  <input
                    type="url"
                    value={newContact.linkedin}
                    onChange={(e) => setNewContact(prev => ({ ...prev, linkedin: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    placeholder="LinkedIn URL"
                  />
                </div>

                {/* Social Media Links Section */}
                <div className="border-t border-slate-600 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-slate-300">Social Media Links</label>
                    <Button
                      type="button"
                      onClick={handleAddSocialMedia}
                      size="sm"
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Link
                    </Button>
                  </div>
                  
                  {newContact.socialMedia && newContact.socialMedia.length > 0 && (
                    <div className="space-y-3">
                      {newContact.socialMedia.map((social, index) => (
                        <div key={index} className="flex gap-2">
                          <select
                            value={social.platform}
                            onChange={(e) => handleSocialMediaChange(index, 'platform', e.target.value)}
                            className="w-1/3 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                          >
                            <option value="">Select Platform</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Twitter">Twitter/X</option>
                            <option value="TikTok">TikTok</option>
                            <option value="YouTube">YouTube</option>
                            <option value="Pinterest">Pinterest</option>
                            <option value="Snapchat">Snapchat</option>
                            <option value="WhatsApp">WhatsApp</option>
                            <option value="Telegram">Telegram</option>
                            <option value="Discord">Discord</option>
                            <option value="Reddit">Reddit</option>
                            <option value="Other">Other</option>
                          </select>
                          <input
                            type="url"
                            value={social.url}
                            onChange={(e) => handleSocialMediaChange(index, 'url', e.target.value)}
                            placeholder="https://..."
                            className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                          />
                          <Button
                            type="button"
                            onClick={() => handleRemoveSocialMedia(index)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleSaveEditContact}
                  className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button
                  onClick={() => {
                    setShowEditContact(false);
                    setEditingContactIndex(null);
                  }}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactGroupForm;
